import { useEffect, useMemo, useState } from "react";
import { Save, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Loading, ExportButton } from "@/components/Common";
import { FilterBar, useRowFilter } from "@/components/Filters";
import { TypeBadge, WarehouseBadge } from "@/components/Badges";
import { AsinSalesPanel } from "@/components/AsinSales";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useAuth } from "@/context/AuthContext";
import { useLoad } from "@/hooks/useData";
import { api, errMsg, notifyStockChanged } from "@/lib/api";
import { todayStr, dateLabel } from "@/lib/format";

const QtyInput = ({ value, onChange, tone, testid }) => (
  <Input type="number" min="0" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className={`h-8 w-20 text-right font-mono ${value ? tone : ""}`} />
);

export default function DailyEntry() {
  const { isAdmin, prefs, setPref } = useAuth();
  const draft = prefs?.["draft-daily"];
  const [date, setDate] = useState(draft?.date || todayStr());
  const [ref, setRef] = useState(draft?.ref || "");
  const [qty, setQty] = useState(draft?.qty || {});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => {
      const has = Object.keys(qty).length || ref;
      if (has || draft) setPref("draft-daily", has ? { date, ref, qty } : null);
    }, 800);
    return () => clearTimeout(t);
  }, [date, ref, qty]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data: report } = useLoad(() => api.get("/report/products").then((r) => r.data));
  const { data: moves, reload } = useLoad(() => api.get("/movements", { params: { date } }).then((r) => r.data), [date]);
  const f = useRowFilter(report?.rows);

  const dayTotals = useMemo(() => {
    const t = { IN: 0, OUT: 0 };
    (moves || []).forEach((m) => { t[m.type] += m.qty; });
    return t;
  }, [moves]);
  const changed = Object.entries(qty).filter(([, v]) => ["ain", "aout", "bin", "btr"].some((k) => Number(v[k]) > 0));
  const setRow = (id, k) => (v) => setQty((q) => ({ ...q, [id]: { ...q[id], [k]: v } }));

  const save = async () => {
    if (!changed.length) return toast.error("Enter at least one quantity");
    setBusy(true);
    try {
      const entries = changed.flatMap(([product_id, v]) => [
        { product_id, warehouse: "A", qty_in: Number(v.ain) || 0, qty_out: Number(v.aout) || 0 },
        { product_id, warehouse: "B", qty_in: Number(v.bin) || 0, qty_transfer: Number(v.btr) || 0 },
      ]).filter((e) => e.qty_in || e.qty_out || e.qty_transfer);
      const { data } = await api.post("/movements/bulk", { date, warehouse: "A", ref, entries });
      toast.success(`${data.created} entries saved for ${dateLabel(date)}`);
      setQty({});
      setRef("");
      notifyStockChanged();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const del = async (m) => {
    const asin = m.reason === "asin_sale";
    if (!window.confirm(asin ? "This entry is part of an ASIN sale. Delete the whole ASIN sale (all its products) and restore the stock?" : "Delete this entry?")) return;
    try {
      await api.delete(asin ? `/asin-sales/${m.sale_id}` : `/movements/${m.id}`);
      toast.success(asin ? "ASIN sale deleted, stock restored" : "Entry deleted"); notifyStockChanged(); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!report) return <Loading />;
  const n = (x) => Number(x) || 0;
  return (
    <div>
      <PageHeader title="Daily IN / OUT" subtitle="Record inward and outward stock product-wise for a day, for Warehouse A and Warehouse B.">
        <ExportButton kind="movements" params={{ month: date.slice(0, 7) }} label="Export month entries" />
      </PageHeader>
      <div className="panel p-4 mb-4 flex flex-wrap items-end gap-3">
        <div><div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Date</div>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-44" data-testid="entry-date-input" /></div>
        <div><div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Reference (optional)</div>
          <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Invoice / challan no." className="h-9 w-56" data-testid="entry-ref-input" /></div>
        <div className="ml-auto flex gap-6 font-mono text-sm">
          <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Day IN</div><div className="text-emerald-400 text-xl font-bold" data-testid="day-total-in">{dayTotals.IN}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Day OUT</div><div className="text-red-400 text-xl font-bold" data-testid="day-total-out">{dayTotals.OUT}</div></div>
        </div>
      </div>

      <Tabs defaultValue="entry">
        <TabsList className="mb-4">
          <TabsTrigger value="entry" data-testid="tab-bulk-entry">Manual entry (product-wise)</TabsTrigger>
          <TabsTrigger value="asin" data-testid="tab-asin-sales">Daily sales by ASIN (OUT)</TabsTrigger>
          <TabsTrigger value="log" data-testid="tab-day-log">Entries on {dateLabel(date)} ({moves?.length || 0})</TabsTrigger>
        </TabsList>
        <TabsContent value="entry"><ErrorBoundary label="Manual entry tab" testid="tab-entry">
          <FilterBar f={f} total={report.rows.length} />
          <div className="panel overflow-auto max-h-[calc(100vh-420px)] min-h-[300px]">
            <table className="data-table" data-testid="bulk-entry-table">
              <thead>
                <tr>
                  <th rowSpan={2}>Product</th><th rowSpan={2}>Category</th>
                  <th colSpan={3} className="!text-center !text-blue-700 border-l border-border">Warehouse A</th>
                  <th colSpan={3} className="!text-center !text-violet-700 border-l border-border">Warehouse B</th>
                  <th rowSpan={2} className="text-right border-l border-border">Total after</th>
                </tr>
                <tr className="[&>th]:top-[33px]">
                  <th className="text-right border-l border-border">Stock in WH A</th><th className="text-right">IN</th><th className="text-right">OUT (sale)</th>
                  <th className="text-right border-l border-border">Stock in WH B</th><th className="text-right">IN</th><th className="text-right">Transfer → WH A</th>
                </tr>
              </thead>
              <tbody>
                {f.filtered.map((r) => {
                  const v = qty[r.id] || {};
                  const afterA = r.stock_a + n(v.ain) - n(v.aout) + n(v.btr);
                  const afterB = r.stock_b + n(v.bin) - n(v.btr);
                  return (
                    <tr key={r.id}>
                      <td className="max-w-[300px]"><div className="truncate font-medium">{r.name}</div><div className="font-mono text-[10px] text-muted-foreground">{r.sku}</div></td>
                      <td className="text-xs text-muted-foreground">{r.category}</td>
                      <td className={`num border-l border-border font-bold ${afterA < 0 ? "text-red-600" : "text-blue-700"}`} data-testid={`stock-a-${r.sku}`}>{r.stock_a}{afterA !== r.stock_a && <span className="ml-1 text-[10px] text-muted-foreground">→ {afterA}</span>}</td>
                      <td className="text-right"><QtyInput value={v.ain || ""} onChange={setRow(r.id, "ain")} tone="border-emerald-500 text-emerald-700" testid={`in-a-input-${r.sku}`} /></td>
                      <td className="text-right"><QtyInput value={v.aout || ""} onChange={setRow(r.id, "aout")} tone="border-red-500 text-red-700" testid={`out-a-input-${r.sku}`} /></td>
                      <td className={`num border-l border-border font-bold ${afterB < 0 ? "text-red-600" : "text-violet-700"}`} data-testid={`stock-b-${r.sku}`}>{r.stock_b}{afterB !== r.stock_b && <span className="ml-1 text-[10px] text-muted-foreground">→ {afterB}</span>}</td>
                      <td className="text-right"><QtyInput value={v.bin || ""} onChange={setRow(r.id, "bin")} tone="border-emerald-500 text-emerald-700" testid={`in-b-input-${r.sku}`} /></td>
                      <td className="text-right"><QtyInput value={v.btr || ""} onChange={setRow(r.id, "btr")} tone="border-blue-500 text-blue-700" testid={`transfer-b-input-${r.sku}`} /></td>
                      <td className="num font-bold border-l border-border" data-testid={`total-after-${r.sku}`}>{afterA + afterB}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="sticky bottom-0 mt-3 flex items-center justify-between rounded-md border border-border bg-card/95 p-3 backdrop-blur">
            <span className="text-sm text-muted-foreground" data-testid="bulk-changed-count">{changed.length} product(s) with quantities · WH A + WH B · {dateLabel(date)}</span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setQty({})} data-testid="bulk-clear-btn">Clear</Button>
              <Button size="sm" onClick={save} disabled={busy || !changed.length} data-testid="bulk-save-btn"><Save className="h-4 w-4" /> {busy ? "Saving…" : "Save entries"}</Button>
            </div>
          </div>
        </ErrorBoundary></TabsContent>
        <TabsContent value="asin"><ErrorBoundary label="ASIN sales tab" testid="tab-asin">
          <div className="mb-3 text-xs text-muted-foreground" data-testid="asin-wh-note">All sales go out from <b className="text-blue-700">Warehouse A</b>. Move stock from Warehouse B first using "Transfer → WH A".</div>
          <AsinSalesPanel date={date} warehouse="A" refNo={ref} stockRows={report.rows} />
        </ErrorBoundary></TabsContent>
        <TabsContent value="log"><ErrorBoundary label="Entries tab" testid="tab-log">
          <div className="panel overflow-auto">
            <table className="data-table" data-testid="day-log-table">
              <thead><tr><th>Product</th><th>Type</th><th>WH</th><th className="text-right">Qty</th><th>Source</th><th>Reference</th><th>By</th><th></th></tr></thead>
              <tbody>
                {(moves || []).map((m) => (
                  <tr key={m.id} data-testid={`log-row-${m.id}`}>
                    <td className="max-w-[300px] truncate">{m.product_name}</td><td><TypeBadge value={m.type} /></td><td><WarehouseBadge value={m.warehouse} /></td>
                    <td className="num font-bold">{m.qty}</td><td className="text-xs text-muted-foreground">{m.reason.replace("_", " ")}</td>
                    <td className="text-xs">{m.ref}{m.note ? ` · ${m.note}` : ""}</td><td className="text-xs text-muted-foreground">{m.created_by}</td>
                    <td>{isAdmin && (m.reason.endsWith("_order") ? (
                      <span title="Linked to an order. Cancel or edit the order to change this entry." className="inline-block p-1 text-muted-foreground/60" data-testid={`locked-movement-${m.id}`}><Lock className="h-4 w-4" /></span>
                    ) : (m.reason !== "asin_sale" || m.sale_id) && (
                      <button onClick={() => del(m)} className="p-1 text-muted-foreground hover:text-red-500" title="Delete entry" data-testid={`delete-movement-${m.id}`}><Trash2 className="h-4 w-4" /></button>))}</td>
                  </tr>
                ))}
                {!moves?.length && <tr><td colSpan={8} className="py-10 text-center text-muted-foreground">No entries on this date.</td></tr>}
              </tbody>
            </table>
          </div>
        </ErrorBoundary></TabsContent>
      </Tabs>
    </div>
  );
}
